import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ShieldCheck, GlobeHemisphereWest, Certificate, Broadcast } from "@phosphor-icons/react";
import { useLang } from "@/context/LanguageContext";
import { DataFlowBg } from "@/components/DataFlowBg";
import CidesLogo from "@/components/CidesLogo";
import { IMG } from "@/constants/brand";

const StatBlock = ({ n, label }) => (
  <div className="border-l border-[#2AB0EA]/30 pl-5">
    <div className="font-mono text-[#FF6B35] text-sm tracking-wider">{n}</div>
    <div className="text-slate-200 text-sm mt-1 leading-snug">{label}</div>
  </div>
);

const Pillar = ({ Icon, title, desc }) => (
  <div className="sharp-card p-8 relative card-hilite">
    <Icon size={28} className="text-[#2AB0EA]" weight="duotone" />
    <div className="mt-6 text-white text-lg font-heading tracking-tight">{title}</div>
    <p className="mt-3 text-slate-300 text-sm leading-relaxed">{desc}</p>
  </div>
);

const Home = () => {
  const { t } = useLang();

  return (
    <>
      {/* Hero */}
      <section className="relative pt-32 pb-24 md:pt-40 md:pb-32 overflow-hidden" data-testid="home-hero">
        <div className="absolute inset-0">
          <img src={IMG.heroBg} alt="" className="w-full h-full object-cover opacity-[0.12]" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0f1826] via-[#0f1826]/85 to-[#0f1826]" />
        </div>
        <DataFlowBg density={8} />
        <div className="relative">
          {/* Logo: independently centered on the full page width */}
          <div className="w-full flex justify-center" data-testid="hero-logo">
            <CidesLogo height={72} className="opacity-95" />
          </div>

          {/* Content: left-aligned within the constrained container */}
          <div className="max-w-6xl mx-auto px-6 md:px-12 mt-10">
            <div className="section-eyebrow">{t("hero.eyebrow")}</div>
            <h1 className="h-display mt-6 text-4xl sm:text-5xl lg:text-6xl xl:text-7xl text-white max-w-4xl">
              {t("hero.title1")}
              <br />
              <span className="text-[#2AB0EA]">{t("hero.title2")}</span>
            </h1>
            <p className="mt-8 max-w-2xl text-slate-200 text-base sm:text-lg leading-relaxed">
              {t("hero.subtitle")}
            </p>

            <div className="mt-10 flex flex-col sm:flex-row items-start sm:items-center gap-4">
              <Link
                to="/telecom"
                data-testid="hero-primary-cta"
                className="group inline-flex items-center gap-3 bg-[#FF6B35] hover:bg-[#E55A2B] text-white px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-semibold transition-colors"
              >
                {t("hero.cta1")}
                <ArrowRight size={16} className="group-hover:translate-x-1 transition-transform" />
              </Link>
              <Link
                to="/contact"
                data-testid="hero-secondary-cta"
                className="inline-flex items-center gap-3 border border-[#2AB0EA]/50 hover:border-[#2AB0EA] text-[#2AB0EA] hover:bg-[#2AB0EA]/10 px-6 py-3.5 text-xs uppercase tracking-[0.24em] font-medium transition-colors"
              >
                {t("hero.cta2")}
              </Link>
            </div>

            <div className="mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl">
              <StatBlock n="/ 01" label={t("hero.stat1")} />
              <StatBlock n="/ 02" label={t("hero.stat2")} />
              <StatBlock n="/ 03" label={t("hero.stat3")} />
            </div>
          </div>
        </div>
      </section>

      {/* About */}
      <section className="relative py-24 md:py-32 border-t border-white/[0.08]" data-testid="home-about">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <div className="section-eyebrow">{t("about.eyebrow")}</div>
            <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white">{t("about.title")}</h2>
          </div>
          <div className="lg:col-span-8 space-y-5 text-slate-200 text-base leading-relaxed">
            <p>{t("about.p1")}</p>
            <p>{t("about.p2")}</p>
            <p>{t("about.p3")}</p>

            <figure className="mt-8 border-l-2 border-[#FF6B35] pl-6 py-2">
              <blockquote className="text-white font-heading text-xl md:text-2xl leading-snug italic">
                “{t("about.quote")}”
              </blockquote>
            </figure>
          </div>
        </div>

        <div className="max-w-7xl mx-auto px-6 md:px-12 mt-14 grid md:grid-cols-3 gap-6">
          <Pillar Icon={ShieldCheck} title={t("about.pillar1Title")} desc={t("about.pillar1Desc")} />
          <Pillar Icon={GlobeHemisphereWest} title={t("about.pillar2Title")} desc={t("about.pillar2Desc")} />
          <Pillar Icon={Certificate} title={t("about.pillar3Title")} desc={t("about.pillar3Desc")} />
        </div>
      </section>

      {/* Activities preview */}
      <section className="relative py-24 md:py-32 border-t border-white/[0.08]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="section-eyebrow">{t("nav.home")} · Activities</div>
          <h2 className="h-display mt-5 text-3xl sm:text-4xl text-white max-w-3xl">
            {t("documents.title")} — {t("telecom.title")}
          </h2>

          <div className="mt-14 grid md:grid-cols-2 gap-6">
            <Link
              to="/documents"
              data-testid="home-card-documents"
              className="group sharp-card overflow-hidden"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={IMG.securityDocs} alt="Secure documents" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#17223a] to-transparent" />
                <div className="absolute top-4 left-4 font-mono text-[#FF6B35] text-xs">/ 01</div>
              </div>
              <div className="p-6">
                <div className="section-eyebrow">{t("documents.eyebrow")}</div>
                <div className="mt-3 text-white text-xl font-heading">{t("documents.title")}</div>
                <div className="mt-6 inline-flex items-center gap-2 text-[#2AB0EA] text-xs uppercase tracking-[0.24em] group-hover:gap-3 transition-all">
                  {t("hero.cta1")} <ArrowRight size={14} />
                </div>
              </div>
            </Link>

            <Link
              to="/telecom"
              data-testid="home-card-telecom"
              className="group sharp-card overflow-hidden"
            >
              <div className="relative aspect-[16/9] overflow-hidden">
                <img src={IMG.telecomTower} alt="Telecom" className="w-full h-full object-cover opacity-70 group-hover:opacity-90 transition-opacity" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#17223a] to-transparent" />
                <div className="absolute top-4 left-4 font-mono text-[#FF6B35] text-xs">/ 02</div>
              </div>
              <div className="p-6">
                <div className="section-eyebrow flex items-center gap-2"><Broadcast size={14} /> {t("telecom.eyebrow")}</div>
                <div className="mt-3 text-white text-xl font-heading">{t("telecom.title")}</div>
                <div className="mt-6 inline-flex items-center gap-2 text-[#2AB0EA] text-xs uppercase tracking-[0.24em] group-hover:gap-3 transition-all">
                  {t("hero.cta1")} <ArrowRight size={14} />
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </>
  );
};

export default Home;
